import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import {
    sendOtp,
    verifyOtp,
    isLoggedIn,
    logOut,
    signUp,
    login,
    forgotPassword,
    resetPassword,
    checkUniqueNumber,
    checkUniqueEmail,
    getToken,
    googleSSOLogin,
} from './login.service'

const PENDING_WIDGET_REDIRECT_KEY = 'pendingWidgetRedirect'

const readPendingWidgetRedirectFromStorage = () => {
    try {
        const raw = sessionStorage.getItem(PENDING_WIDGET_REDIRECT_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        if (parsed?.pathname) return parsed
    } catch {
        // ignore
    }
    return null
}

const initialState = {
    status: 'loading',
    userProfile: undefined,
    /** Full return target after login when user opened a ?mode=widget URL while logged out */
    pendingWidgetRedirect: readPendingWidgetRedirectFromStorage(),
    moduleContainers: [],
    timer: {
        seconds: '00',
        minutes: '00',
        hours: '00',
    },
    socketClient: {},
    token: null,
    emailDisable: false,
}

export const sendOtpLogin = createAsyncThunk(
    'loginSlice/sendOtp',
    async (payload = {}) => {
        const response = await sendOtp(payload)
        return response
    }
)

export const verifyOtpLogin = createAsyncThunk(
    'loginSlice/verifyOtp',
    async (payload) => {
        const response = await verifyOtp(payload)
        return response
    }
)

export const isLoggedInLogin = createAsyncThunk(
    'loginSlice/isLoggedIn',
    async (payload = {}) => {
        const response = await isLoggedIn(payload)
        if (response?.data?.success) {
            const token = response?.data?.token

            // ✅ Store token in localStorage and sessionStorage
            if (token) {
                localStorage.setItem('token', token)
                sessionStorage.setItem('token', token)
            }
            localStorage.setItem(
                'currentProject',
                JSON.stringify({
                    id: response?.data?.data.activeProject?._id,
                    name: response?.data?.data?.activeProject?.name,
                })
            )
            return response
        }
        return response
    }
)

export const tokenGet = createAsyncThunk(
    'loginSlice/getToken',
    async (payload = {}) => {
        const response = await getToken(payload)
        return response
    }
)

export const signOut = createAsyncThunk(
    'loginSlice/logOut',
    async (payload = {}) => {
        const response = await logOut(payload)
        return response
    }
)

export const signUpForm = createAsyncThunk(
    'loginSlice/signUp',
    async (payload = {}) => {
        const response = await signUp(payload)
        return response
    }
)

export const loginGoogleSSO = createAsyncThunk(
    'loginSlice/googleSSOLogin',
    async (payload = {}) => {
        const response = await googleSSOLogin(payload)
        return response
    }
)

export const loginForm = createAsyncThunk(
    'loginSlice/login',
    async (payload = {}) => {
        const response = await login(payload)
        return response
    }
)

export const passwordForgot = createAsyncThunk(
    'loginSlice/forgotPassword',
    async (payload = {}) => {
        const response = await forgotPassword(payload)
        return response
    }
)

export const passwordReset = createAsyncThunk(
    'loginSlice/resetPassword',
    async (payload = {}) => {
        const response = await resetPassword(payload)
        return response
    }
)

export const numberCheck = createAsyncThunk(
    'loginSlice/checkUniqueNumber',
    async (payload = {}) => {
        const response = await checkUniqueNumber(payload)
        return response
    }
)

export const emailCheck = createAsyncThunk(
    'loginSlice/checkUniqueEmail',
    async (payload = {}) => {
        const response = await checkUniqueEmail(payload)
        return response
    }
)

export const loginSlice = createSlice({
    name: 'loginSlice',
    initialState,
    reducers: {
        setUserProfile: (state, action) => {
            state.userProfile = action.payload
        },
        setEmailDisable: (state, action) => {
            state.emailDisable = action.payload
        },
        setSocketClient: (state, action) => {
            state.socketClient = action.payload.socket
            state.token = action.payload.token
        },
        setPendingWidgetRedirect: (state, action) => {
            state.pendingWidgetRedirect = action.payload
            try {
                if (action.payload?.pathname) {
                    sessionStorage.setItem(
                        PENDING_WIDGET_REDIRECT_KEY,
                        JSON.stringify(action.payload)
                    )
                } else {
                    sessionStorage.removeItem(PENDING_WIDGET_REDIRECT_KEY)
                }
            } catch {
                // ignore
            }
        },
        clearPendingWidgetRedirect: (state) => {
            state.pendingWidgetRedirect = null
            try {
                sessionStorage.removeItem(PENDING_WIDGET_REDIRECT_KEY)
            } catch {
                // ignore
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(isLoggedInLogin.pending, (state, action) => {
                // state.status = 'loading'
            })
            .addCase(isLoggedInLogin.fulfilled, (state, action) => {
                state.status = 'loaded'
                const success = action.payload?.data?.success
                if (success) {
                    state.moduleContainers =
                        action.payload?.data?.data?.role?.containers
                    state.userProfile = action.payload.data.data
                } else {
                    state.moduleContainers = []
                    state.userProfile = undefined
                }
            })
            .addCase(signOut.fulfilled, (state) => {
                state.pendingWidgetRedirect = null
                try {
                    sessionStorage.removeItem(PENDING_WIDGET_REDIRECT_KEY)
                } catch {
                    // ignore
                }
            })
    },
})

export default loginSlice.reducer

/** Return the slice reference — never rebuild an object (avoids rerender warnings). */
export const loginState = (state) => state.loginSlice || initialState

export const isUserAuthenticated = (userProfile) => Boolean(userProfile?._id)

export const { setUserProfile, setSocketClient, setEmailDisable, setPendingWidgetRedirect, clearPendingWidgetRedirect } =
    loginSlice.actions
